import { Pencil, Trash2 } from 'lucide-react';
import {
  formatDayOfMonthLabel,
  formatPayrollAdjustmentEuro,
  type PayrollAdjustmentCategory,
  type PayrollAdjustmentRuleRow,
} from '../../../utils/payrollAdjustmentCategories';
import { yearMonthLabel } from './PayrollAdjustmentBookingDrawer';

type PayrollAdjustmentRulesTableProps = {
  rules: PayrollAdjustmentRuleRow[];
  categoryByKey: Map<string, PayrollAdjustmentCategory>;
  onEditRule: (ruleId: string) => void;
  onDeleteRule: (ruleId: string) => void;
  emptyMessage?: string;
};

export function PayrollAdjustmentRulesTable({
  rules,
  categoryByKey,
  onEditRule,
  onDeleteRule,
  emptyMessage = 'Noch keine regelmäßigen Buchungen angelegt.',
}: PayrollAdjustmentRulesTableProps) {
  return (
    <div className="overflow-hidden rounded-lg border border-app-border bg-app-surface">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr>
            {['Mitarbeiter', 'Kategorie', 'Tag', 'Beginnt ab', 'Endet', 'Status', 'Betrag / Monat', ''].map(
              (h) => (
                <th
                  key={h || 'actions'}
                  scope="col"
                  className={`sticky top-0 z-10 border-b border-app-border bg-app-surface-muted px-3.5 py-2.5 text-xs font-medium text-app-text-muted ${
                    h === 'Betrag / Monat' ? 'text-right' : 'text-left'
                  }`}
                >
                  {h}
                </th>
              ),
            )}
          </tr>
        </thead>
        <tbody>
          {rules.length === 0 ? (
            <tr>
              <td colSpan={8} className="px-4 py-10 text-center text-sm text-app-text-subtle">
                {emptyMessage}
              </td>
            </tr>
          ) : (
            rules.map((rule) => {
              const cat = categoryByKey.get(rule.category_key);
              const isBezug = cat?.type === 'bezug';
              return (
                <tr key={rule.id} className="border-b border-app-border last:border-0">
                  <td className="px-3.5 py-2.5 font-medium text-app-text">{rule.employee_name}</td>
                  <td className="px-3.5 py-2.5 text-app-text-muted">
                    <div className="flex items-center gap-2">
                      <span
                        className={`h-2 w-2 rounded-full ${isBezug ? 'bg-emerald-500' : 'bg-red-500'}`}
                      />
                      {cat?.label ?? rule.category_key}
                    </div>
                  </td>
                  <td className="whitespace-nowrap px-3.5 py-2.5 text-app-text-muted">
                    {formatDayOfMonthLabel(rule.day_of_month)}
                  </td>
                  <td className="px-3.5 py-2.5 text-app-text-muted">{yearMonthLabel(rule.start_year_month)}</td>
                  <td className="px-3.5 py-2.5 text-app-text-muted">
                    {rule.end_mode === 'exit'
                      ? 'bis Austritt'
                      : rule.end_year_month
                        ? yearMonthLabel(rule.end_year_month)
                        : '-'}
                  </td>
                  <td className="px-3.5 py-2.5">
                    <span
                      className={`rounded px-2 py-1 text-xs font-semibold ${
                        rule.active ? 'bg-emerald-50 text-emerald-700' : 'bg-app-surface-muted text-app-text-subtle'
                      }`}
                    >
                      {rule.active ? 'Aktiv' : 'Beendet'}
                    </span>
                  </td>
                  <td
                    className={`px-3.5 py-2.5 text-right font-semibold ${
                      isBezug ? 'text-emerald-600' : 'text-red-600'
                    }`}
                  >
                    {isBezug ? '+ ' : '− '}
                    {formatPayrollAdjustmentEuro(rule.amount)}
                  </td>
                  <td className="px-3.5 py-2.5">
                    <div className="flex justify-end gap-1">
                      <button
                        type="button"
                        title="Bearbeiten"
                        onClick={() => onEditRule(rule.id)}
                        className="flex h-7 w-7 items-center justify-center rounded text-app-text-subtle hover:bg-app-hover"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      <button
                        type="button"
                        title="Löschen"
                        onClick={() => void onDeleteRule(rule.id)}
                        className="flex h-7 w-7 items-center justify-center rounded text-app-text-subtle hover:bg-red-50 hover:text-red-600"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}
